import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { DataGrid, GridToolbar, getGridStringOperators } from '@mui/x-data-grid';
import axios from 'axios';
import flaskServer from '../config/flaskserver';
import { Select, MenuItem, Checkbox, ListItemText, Chip, Button, Typography, Box } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FilterListIcon from '@mui/icons-material/FilterList';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';


// Operadores de string customizados para permitir que "isAnyOf" funcione como "contains any of"
export const customStringOperators = getGridStringOperators().map(operator => {
  if (operator.value === 'isAnyOf') {
    return {
      ...operator,
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value || filterItem.value.length === 0) {
          return null;
        }
        return (params) => {
          // No MUI X v6/v7, dependendo da configuração, params pode ser o valor direto 
          // ou um objeto { value, row, column, ... }
          const cellValueRaw = (params && typeof params === 'object' && 'value' in params)
            ? params.value
            : params;

          if (cellValueRaw === null || cellValueRaw === undefined) return false;

          const cellValue = cellValueRaw.toString().toLowerCase();
          return filterItem.value.some(val => {
            if (!val) return false;
            return cellValue.includes(val.toString().toLowerCase().trim());
          });
        };
      },
    };
  }
  return operator;
});


export const stringToColor = (string) => {
  let hash = 0;
  let i;
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

export const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    // Return in pt-BR format (dd/mm/yyyy)
    return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(date);
  }
  return dateString;
};

export const getRowClassName = (rowData) => {
  if (rowData['Conciliacao Histórico'] === 'OK' && rowData['Conciliacao Valor'] === 'OK') {
    return 'Conciliado';
  } else if (rowData['Conciliacao Histórico'] === 'OK' || rowData['Conciliacao Valor'] === 'OK') {
    return 'Parcialmente-Conciliado';
  } else {
    return 'Não-Conciliado';
  }
};



const TabelaProcessada = ({ dados, onUpdate }) => {
  const [filtroCor, setFiltroCor] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });

  const handleFilterModelChange = useCallback((newModel) => {
    console.log('[DataGrid] Filter Model Change:', newModel);
    let needsUpdate = false;
    const items = newModel.items.map(item => {
      // Se o operador for "isAnyOf" (é qualquer um de), verificamos se há strings delimitadas
      if (item.operator === 'isAnyOf' && Array.isArray(item.value)) {
        const newValues = [];
        let itemChanged = false;

        item.value.forEach(val => {
          if (typeof val === 'string' && (val.includes(',') || val.includes(';') || val.includes('\n') || val.includes('\t'))) {
            // Separa por vírgula, ponto e vírgula, tabulação ou quebra de linha
            const split = val.split(/[ ,;\t\n]+/).filter(v => v.trim() !== '');
            newValues.push(...split);
            itemChanged = true;
            needsUpdate = true;
          } else {
            newValues.push(val);
          }
        });

        if (itemChanged) {
          // Remove duplicatas e retorna o item atualizado
          return { ...item, value: [...new Set(newValues)] };
        }
      }
      return item;
    });

    if (needsUpdate) {
      setFilterModel({ ...newModel, items });
    } else {
      setFilterModel(newModel);
    }
  }, []);

  useEffect(() => {
    if (dados && dados.length > 0) {
      // Usar a linha como base para o ID, dando preferência se já existir um campo 'id' estável,
      // senão criar um baseado no índice.
      const dadosComId = dados.map((row, index) => {
        // Se a linha já tem um ID numérico ou string estável, mantemos.
        // Se não, geramos um prefixado para evitar colisão.
        const rowId = row.id !== undefined ? `data-${row.id}` : `row-${index}`;
        return { ...row, id: rowId };
      });
      setTableData(dadosComId);
    } else {
      setTableData([]);
    }
  }, [dados]);

  const columns = useMemo(() => {
    const fixedColumns = [
      {
        field: 'Data', headerName: 'Data', width: 120, editable: true,
        renderCell: (params) => formatDate(params.value)
      },
      {
        field: 'Débitos', headerName: 'Débitos', width: 130, editable: true, type: 'number',
        renderCell: (params) => {
          const val = parseFloat(params.value);
          if (isNaN(val) || val === 0) return '-';
          return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
        }
      },
      {
        field: 'Créditos', headerName: 'Créditos', width: 130, editable: true, type: 'number',
        renderCell: (params) => {
          const val = parseFloat(params.value);
          if (isNaN(val) || val === 0) return '-';
          return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
        }
      },
      { field: 'Registro', headerName: 'Registro', width: 100, editable: true, filterOperators: customStringOperators },
      { field: 'Histórico', headerName: 'Histórico', width: 250, editable: true, filterOperators: customStringOperators },
      {
        field: 'Conciliacao Histórico',
        headerName: 'Conciliação Histórico',
        width: 170,
        editable: false,
        renderCell: (params) => {
          const isOk = params.value === 'OK';
          return (
            <Box display="flex" alignItems="center" gap={0.5}>
              {isOk ? <CheckCircleOutlineIcon color="success" fontSize="small" /> : <ErrorOutlineIcon color="error" fontSize="small" />}
              <Typography variant="body2" color={isOk ? "success.main" : "error.main"} fontWeight={500}>
                {params.value}
              </Typography>
            </Box>
          );
        }
      },
      {
        field: 'Conciliacao Valor',
        headerName: 'Conciliação Valor',
        width: 170,
        editable: false,
        renderCell: (params) => {
          const isOk = params.value === 'OK';
          return (
            <Box display="flex" alignItems="center" gap={0.5}>
              {isOk ? <CheckCircleOutlineIcon color="success" fontSize="small" /> : <ErrorOutlineIcon color="error" fontSize="small" />}
              <Typography variant="body2" color={isOk ? "success.main" : "error.main"} fontWeight={500}>
                {params.value}
              </Typography>
            </Box>
          );
        }
      },
      {
        field: 'Observacao',
        headerName: 'Observação',
        width: 180,
        editable: false,
        renderCell: (params) => (
          params.value ? (
            <Chip
              label={params.value === 'Conciliado pela IA' ? '🤖 IA' : params.value}
              color={params.value === 'Conciliado pela IA' ? 'info' : 'default'}
              variant="filled"
              size="small"
            />
          ) : null
        )
      },
      {
        field: 'Grupo Conciliacao',
        headerName: 'ID Conciliação',
        width: 130,
        editable: false,
        renderCell: (params) => (
          params.value ? (
            <Chip
              label={params.value}
              sx={{
                borderColor: stringToColor(params.value),
                color: stringToColor(params.value),
                fontWeight: 'bold'
              }}
              variant="outlined"
              size="small"
            />
          ) : null
        )
      },
      {
        field: 'Confianca IA',
        headerName: 'Confiança',
        width: 110,
        editable: false,
        renderCell: (params) => {
          if (!params.value) return null;

          const getColor = (conf) => {
            if (conf === 'Alta') return 'success';
            if (conf === 'Média') return 'warning';
            return 'error';
          };

          return (
            <Chip
              label={params.value}
              color={getColor(params.value)}
              size="small"
              variant="outlined"
            />
          );
        }
      },
    ];

    const dynamicColumns = tableData && tableData.length > 0
      ? Object.keys(tableData[0])
        .filter(key => key !== 'id' && key !== 'Razao_Social_Origem' && !fixedColumns.some(col => col.field === key))
        .map(key => {
          if (key === 'Razao Social' || key === 'Razão Social') {
            return {
              field: key,
              headerName: 'Razão Social',
              width: 280,
              editable: true,
              renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {params.value}
                  </Typography>
                  {params.row['Razao_Social_Origem'] === 'IA' && (
                    <Chip
                      icon={<AutoAwesomeIcon sx={{ fontSize: '12px !important' }} />}
                      label="IA"
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.65rem', fontWeight: 'bold' } }}
                    />
                  )}
                </Box>
              )
            };
          }
          return { field: key, headerName: key, width: 150, editable: true };
        })
      : [];

    return [...fixedColumns, ...dynamicColumns];
  }, [tableData]);

  const processRowUpdate = async (updatedRow, originalRow) => {
    try {
      const response = await axios.post(`${flaskServer.flaskServerIP}/validarcampos`, { data: updatedRow });
      if (response.status === 200) {
        const newData = tableData.map(row => (row.id === originalRow.id ? updatedRow : row));
        setTableData(newData);
        onUpdate(newData);
        return updatedRow;
      }
    } catch (error) {
      console.error("Erro ao atualizar linha:", error);
      // Reverte a alteração em caso de erro (opcional)
    }
    return originalRow;
  };

  const handleProcessRowUpdateError = (error) => {
    console.error("Erro no processamento da linha:", error);
  };

  const filteredRows = useMemo(() => {
    if (filtroCor.length === 0) return tableData;
    return tableData.filter(row => {
      const className = getRowClassName(row);
      if (filtroCor.includes('IA')) {
        return row['Observacao'] && row['Observacao'].includes('IA');
      }
      return filtroCor.includes(className);
    });
  }, [tableData, filtroCor]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <FilterListIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, mr: 1 }}>Filtros:</Typography>

        <Button
          variant={filtroCor.includes('Não-Conciliado') ? "contained" : "outlined"}
          color="error"
          size="small"
          startIcon={<ErrorOutlineIcon />}
          onClick={() => setFiltroCor(prev => prev.includes('Não-Conciliado') ? [] : ['Não-Conciliado'])}
          sx={{ borderRadius: 20, textTransform: 'none', px: 2 }}
        >
          Pendentes
        </Button>
        <Button
          variant={filtroCor.includes('Conciliado') ? "contained" : "outlined"}
          color="success"
          size="small"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={() => setFiltroCor(prev => prev.includes('Conciliado') ? [] : ['Conciliado'])}
          sx={{ borderRadius: 20, textTransform: 'none', px: 2 }}
        >
          Conciliados
        </Button>
        <Button
          variant={filtroCor.includes('Parcialmente-Conciliado') ? "contained" : "outlined"}
          color="warning"
          size="small"
          startIcon={<FilterListIcon />}
          onClick={() => setFiltroCor(prev => prev.includes('Parcialmente-Conciliado') ? [] : ['Parcialmente-Conciliado'])}
          sx={{ borderRadius: 20, textTransform: 'none', px: 2 }}
        >
          Parciais
        </Button>
        <Button
          variant={filtroCor.includes('IA') ? "contained" : "outlined"}
          color="info"
          size="small"
          startIcon={<SmartToyIcon />}
          onClick={() => setFiltroCor(prev => prev.includes('IA') ? [] : ['IA'])}
          sx={{ borderRadius: 20, textTransform: 'none', px: 2 }}
        >
          Conciliado por IA
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          size="small"
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={() => {
            sessionStorage.setItem('previewTableData', JSON.stringify(filteredRows));
            window.open('/preview-tabela', '_blank');
          }}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.75rem',
            borderColor: 'rgba(0,0,0,0.12)',
            color: 'text.primary',
            '&:hover': { borderColor: 'rgba(0,0,0,0.24)', bgcolor: 'rgba(0,0,0,0.04)' }
          }}
        >
          Ver em Nova Aba
        </Button>

        {filtroCor.length > 0 && (
          <Button
            size="small"
            onClick={() => setFiltroCor([])}
            sx={{ borderRadius: 20, textTransform: 'none', color: 'text.secondary' }}
          >
            Limpar filtros
          </Button>
        )}
      </Box>

      <div style={{ height: '75vh', width: '100%' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          getRowClassName={(params) => getRowClassName(params.row)}
          sx={{
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: '#fafafa',
            },
            '& .Conciliado': {
              bgcolor: '#f1f8e9',
              '&:hover': { bgcolor: '#e8f5e9' }
            },
            '& .Não-Conciliado': {
              bgcolor: '#fff5f5',
              '&:hover': { bgcolor: '#ffebee' }
            },
            '& .Parcialmente-Conciliado': {
              bgcolor: '#fffde7',
              '&:hover': { bgcolor: '#fff9c4' }
            },
            border: 0,
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
            columns: {
              columnVisibilityModel: {
                // Keep these visible by default
                'Data': true,
                'Débitos': true,
                'Créditos': true,
                'Histórico': true,
                'Conciliacao Valor': true,
                'Conciliacao Histórico': true,
                'Registro': true,
                // The rest will be hidden by default but can be enabled in the UI
                'Observacao': false,
                'Observação': false,
                'Grupo Conciliacao': false,
                'Confianca IA': false,
                'Razao Social': false,
                'Razão Social': false,
              }
            }
          }}
          pageSizeOptions={[10, 25, 50, 100, { value: filteredRows.length > 0 ? filteredRows.length : 100, label: 'Todos' }]}
          localeText={{
            noRowsLabel: 'Nenhuma linha',
            noResultsOverlayLabel: 'Nenhum resultado encontrado.',
            errorOverlayDefaultLabel: 'Ocorreu um erro.',
            toolbarDensity: 'Densidade',
            toolbarDensityLabel: 'Densidade',
            toolbarDensityCompact: 'Compacto',
            toolbarDensityStandard: 'Padrão',
            toolbarDensityComfortable: 'Confortável',
            toolbarColumns: 'Colunas',
            toolbarColumnsLabel: 'Exibir colunas',
            toolbarFilters: 'Filtros',
            toolbarFiltersLabel: 'Exibir filtros',
            toolbarFiltersTooltipShow: 'Exibir filtros',
            toolbarFiltersTooltipHide: 'Ocultar filtros',
            toolbarExport: 'Exportar',
            toolbarExportLabel: 'Exportar',
            toolbarExportCSV: 'Baixar como CSV',
            toolbarExportPrint: 'Imprimir',
            columnsPanelTextFieldLabel: 'Encontrar coluna',
            columnsPanelTextFieldPlaceholder: 'Título da coluna',
            columnsPanelDragIconLabel: 'Reordenar coluna',
            columnsPanelShowAllButton: 'Mostrar todas',
            columnsPanelHideAllButton: 'Ocultar todas',
            filterPanelAddFilter: 'Adicionar filtro',
            filterPanelDeleteIconLabel: 'Excluir',
            filterPanelLinkOperator: 'Operador lógico',
            filterPanelOperators: 'Operador',
            filterPanelOperatorAnd: 'E',
            filterPanelOperatorOr: 'Ou',
            filterPanelColumns: 'Colunas',
            filterPanelInputLabel: 'Valor',
            filterPanelInputPlaceholder: 'Filtrar valor',
            filterOperatorContains: 'contém',
            filterOperatorEquals: 'é igual a',
            filterOperatorStartsWith: 'começa com',
            filterOperatorEndsWith: 'termina com',
            filterOperatorIsEmpty: 'está vazio',
            filterOperatorIsNotEmpty: 'não está vazio',
            filterOperatorIsAnyOf: 'é qualquer um de',
            columnMenuLabel: 'Menu',
            columnMenuShowColumns: 'Mostrar colunas',
            columnMenuFilter: 'Filtrar',
            columnMenuHideColumn: 'Ocultar',
            columnMenuUnsort: 'Desfazer ordenação',
            columnMenuSortAsc: 'Ordenar Crescente',
            columnMenuSortDesc: 'Ordenar Decrescente',
            footerRowSelected: (count) =>
              count !== 1
                ? `${count.toLocaleString()} linhas selecionadas`
                : `${count.toLocaleString()} linha selecionada`,
            footerTotalRows: 'Total de linhas:',
            footerTotalVisibleRows: (visibleCount, totalCount) =>
              `${visibleCount.toLocaleString()} de ${totalCount.toLocaleString()}`,
          }}
          components={{
            Toolbar: GridToolbar,
          }}
        />
      </div>
    </Box >
  );
};

export default TabelaProcessada;
